// generateMaterial / snapshot — the job's wire view-model for the side view
// (design v2.1 §5): job phase + per-part state, plain JSON. A PURE projection
// of the job state over the live domain state (titles). The zod schema is
// the contract (`showGenerationJob.outputSchema`); a client parses it at its
// boundary.
export { generationJobSnapshotDto, mkAskToContinueNotice, mkEndSummaryNotice, toSnapshot, };
import { exhaustSwitchCase, Maybe } from "@bilda/utils";
import { z } from "zod/v4";
// The chat messages the job owes (alarm discipline: one end summary, plus a
// message only when a decision is needed). Carried on the snapshot; see the
// runner header for why they are not conversation messages yet.
const zNotice = z.discriminatedUnion("type", [
    z.object({
        type: z.literal("generation_notice_end_summary"),
        landed: z.array(z.object({ part: z.string(), version: z.number() })),
    }),
    z.object({
        type: z.literal("generation_notice_ask_to_continue"),
        remaining: z.array(z.string()),
    }),
]);
const mkEndSummaryNotice = (landed) => ({
    type: "generation_notice_end_summary",
    landed: landed.map((p) => ({ part: `${p.part}`, version: p.version })),
});
const mkAskToContinueNotice = (remaining) => ({
    type: "generation_notice_ask_to_continue",
    remaining: remaining.map((id) => `${id}`),
});
const zOutcome = z.enum(["passed", "needs_attention"]);
const zPartCheck = z.discriminatedUnion("name", [
    // The template's Rules held (the gate the repair loop solves).
    z.object({
        name: z.literal("template_rules"),
        evaluator: z.literal("computed"),
        outcome: zOutcome,
    }),
    // Questions and passages backed by a knowledge-bank claim
    // (`statements`: the factual items, `unsupported`: those no claim backs).
    z.object({
        name: z.literal("citations"),
        evaluator: z.literal("computed"),
        outcome: zOutcome,
        statements: z.number(),
        unsupported: z.number(),
    }),
    // Knowledge the part needed that no claim holds, in the model's words.
    z.object({
        name: z.literal("knowledge_gaps"),
        evaluator: z.literal("computed"),
        outcome: zOutcome,
        gaps: z.array(z.string()),
    }),
    // Sections whose text is very hard to read (the policy's LIX limit).
    z.object({
        name: z.literal("readability"),
        evaluator: z.literal("computed"),
        outcome: zOutcome,
        heavySections: z.array(z.string()),
    }),
    // Does the part do what its brief planned?
    z.object({
        name: z.literal("covers_brief"),
        evaluator: z.literal("judged"),
        outcome: zOutcome,
        note: z.string(),
    }),
    // Does the part say only what its sources support?
    z.object({
        name: z.literal("faithful_to_sources"),
        evaluator: z.literal("judged"),
        outcome: zOutcome,
        note: z.string(),
    }),
]);
const zPart = z.object({
    id: z.string(),
    title: z.string(),
    // `discarded`: the author changed the part meanwhile; their version
    // stays (user wins).
    state: z.enum([
        "queued",
        "running",
        "landed",
        "failed",
        "cancelled",
        "discarded",
    ]),
    // Attempts started so far (the current one while running).
    attempt: z.number(),
    // The version the job landed (or the author's kept version).
    version: z.number().nullable(),
    // Why the last attempt failed.
    reason: z.string().nullable(),
    // The landed version's checks; null until a version landed. Absent on
    // the wire (a server before phase 4) reads as null: additive.
    checks: z.array(zPartCheck).nullable().default(null),
});
const generationJobSnapshotDto = z.object({
    root: z.string(),
    // `interrupted`: the log holds a run with no live job behind it (a server
    // restart cut it short, `interruption.ts`); Continue writes the rest.
    // `reading`: the root held no claim yet, so the job first reads its
    // sources into claims (read on demand); `read_failed`: that read
    // recorded none (`readFailure` says why), and nothing was written.
    phase: z.enum([
        "reading",
        "running",
        "stopping",
        "paused_budget",
        "stopped",
        "done",
        "read_failed",
        "interrupted",
    ]),
    // Why the read recorded no claim; null unless `read_failed`. Absent on
    // the wire (an older server) reads as null: additive.
    readFailure: z
        .enum(["sources_unreadable", "no_claims_found", "reading_failed"])
        .nullable()
        .default(null),
    // Generations left before the job pauses and asks.
    budget: z.number(),
    // Reading order.
    parts: z.array(zPart),
    notices: z.array(zNotice),
});
const toSnapshot = (job, notices, state, checks) => ({
    root: `${job.root}`,
    phase: job.phase,
    readFailure: Maybe.getOrElse(null)(job.readFailure),
    budget: job.budget,
    parts: job.parts.map((p) => ({
        id: `${p.id}`,
        title: titleOf(state, p.id),
        ...partView(p.status),
        checks: checksOf(p.status, checks.get(`${p.id}`)),
    })),
    notices: [...notices],
});
// A deleted or missing part keeps its id as the title (graceful
// degradation; the side view still shows the row).
const titleOf = (state, id) => {
    const r = state.resources.resources.get(id);
    return r !== undefined && r.type === "material" ? `${r.title}` : `${id}`;
};
const checksOf = (s, record) => s.type === "part_landed" &&
    record !== undefined &&
    record.version === s.version
    ? [...record.items]
    : null;
const partView = (s) => {
    switch (s.type) {
        case "part_queued":
            return view("queued", s.attempts, null, null);
        case "part_running":
            return view("running", s.attempt, null, null);
        case "part_failed":
            return view("failed", s.attempts, null, s.reason);
        // Aborting is transient: the runner acknowledges an abort at once.
        case "part_aborting":
            return view("cancelled", s.attempt, null, null);
        case "part_landed":
            return view("landed", s.attempts, s.version, null);
        case "part_cancelled":
            return view("cancelled", 0, null, null);
        case "part_discarded":
            return view("discarded", s.attempts, s.version, null);
        default:
            return exhaustSwitchCase(s);
    }
};
const view = (state, attempt, version, reason) => ({
    state,
    attempt,
    version,
    reason,
});
//# sourceMappingURL=snapshot.js.map